import type {
  AugmentedWarViewerState,
  CellId,
} from "@cloudnest/augmented-war-engine";
import { CELLS, inHomeZone } from "@cloudnest/augmented-war-engine";
import "../styles/augmented-war-tokens.css";

/**
 * Read-only hex board for Augmented War replay (SVG; no interaction).
 *
 * Colours come from --cn-aw-* custom properties, generated from
 * packages/shared-theme/tokens/augmented-war.json. SVG presentation attributes
 * cannot resolve var(), so every colour is applied through `style` instead of
 * a `fill`/`stroke` attribute.
 */
const token = (name: string): string => `var(--cn-aw-${name})`;

const SIZE = 34;
const SQ = Math.sqrt(3);
const cx = (q: number): number => SIZE * 1.5 * q;
const cy = (q: number, r: number): number => SIZE * SQ * (r + q / 2);

interface Layout {
  id: CellId;
  x: number;
  y: number;
  kind: "empty" | "node" | "home";
  owner?: "p1" | "p2";
}

const LAYOUT: readonly Layout[] = Object.values(CELLS).map((c) => ({
  id: c.id,
  x: cx(c.q),
  y: cy(c.q, c.r),
  kind: c.kind,
  owner: c.owner,
}));

const BOUNDS = (() => {
  const xs = LAYOUT.map((c) => c.x);
  const ys = LAYOUT.map((c) => c.y);
  const pad = SIZE * 1.25;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  return {
    viewBox: `${minX} ${minY} ${Math.max(...xs) - minX + pad} ${Math.max(...ys) - minY + pad}`,
  };
})();

function hexPoints(x: number, y: number): string {
  const pts: string[] = [];
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 180) * (60 * k);
    pts.push(
      `${(x + SIZE * Math.cos(a)).toFixed(2)},${(y + SIZE * Math.sin(a)).toFixed(2)}`,
    );
  }
  return pts.join(" ");
}

const PLAYER_COLOR: Readonly<Record<string, string>> = {
  p1: token("p1"),
  p2: token("p2"),
};
const ELEMENT_GLYPH: Readonly<Record<string, string>> = {
  fire: "불",
  water: "물",
  plant: "풀",
  stone: "돌",
  ice: "얼",
};

function cellFill(kind: Layout["kind"], owner?: "p1" | "p2"): string {
  if (kind === "home")
    return owner === "p1" ? token("home1-tint") : token("home2-tint");
  if (kind === "node") return token("cell-node");
  return token("cell-plain");
}

const who = (p: string): string => (p === "p1" ? "P1" : "P2");

interface BoardProps {
  /** Accepts the full state (replay) or a per-viewer projection (live). */
  readonly state: AugmentedWarViewerState;
  /** Interactive mode (GameScreen): fired when a `selectable` cell is clicked. */
  readonly onCellClick?: (id: CellId) => void;
  /** Cells the current player may click right now (rendered clickable). */
  readonly selectableCells?: ReadonlySet<CellId>;
  /** The player's currently selected own unit. */
  readonly selectedCell?: CellId | null;
  /** A cell to emphasise (e.g. the unit under a pending attack, for the defender). */
  readonly dangerCell?: CellId | null;
  readonly testId?: string;
}

export function Board({
  state,
  onCellClick,
  selectableCells,
  selectedCell,
  dangerCell,
  testId = "augmented-war-replay-board",
}: BoardProps) {
  const byId = new Map(LAYOUT.map((c) => [c.id, c]));
  const combat = state.lastCombat;
  const isSelectable = (id: CellId): boolean =>
    onCellClick !== undefined && (selectableCells?.has(id) ?? false);

  return (
    <svg
      viewBox={BOUNDS.viewBox}
      role="img"
      aria-label="Augmented War board"
      style={{ width: "100%", height: "auto", display: "block" }}
      data-testid={testId}
    >
      <title>Augmented War board</title>
      {LAYOUT.map((c) => {
        const occupied = Boolean(state.board[c.id]);
        return (
          <g key={c.id}>
            <polygon
              points={hexPoints(c.x, c.y)}
              style={{
                fill: cellFill(c.kind, c.owner),
                stroke: token("cell-outline"),
              }}
              strokeWidth={1}
            />
            {inHomeZone(c.id) ? (
              <polygon
                points={hexPoints(c.x, c.y)}
                style={{
                  fill: token("homezone-fill"),
                  stroke: token("homezone-stroke"),
                }}
                strokeWidth={1}
              />
            ) : null}
            {c.kind === "node" ? (
              <circle
                cx={c.x}
                cy={c.y}
                r={SIZE * 0.34}
                style={{ fill: "none", stroke: token("node-ring") }}
                strokeWidth={1.4}
              />
            ) : null}
            {c.kind === "home" && c.owner ? (
              <text
                x={c.x}
                y={c.y - SIZE * 0.62}
                textAnchor="middle"
                fontSize={8}
                style={{ fill: token("label") }}
              >
                {who(c.owner)} home
              </text>
            ) : null}
            {!occupied ? (
              <text
                x={c.x}
                y={c.y + SIZE * 0.66}
                textAnchor="middle"
                fontSize={8}
                style={{ fill: token("label-weak") }}
              >
                {c.id}
              </text>
            ) : null}
          </g>
        );
      })}

      {combat
        ? [combat.attacker, combat.target].map((cellId, i) => {
            const c = byId.get(cellId);
            if (!c) return null;
            return (
              <polygon
                key={`hl-${cellId}`}
                points={hexPoints(c.x, c.y)}
                style={{
                  fill: "none",
                  stroke: i === 0 ? token("attacker") : token("target"),
                }}
                strokeWidth={i === 0 ? 2.5 : 3.5}
                strokeDasharray={i === 0 ? "4 3" : undefined}
              />
            );
          })
        : null}

      {Object.entries(state.board).map(([id, unit]) => {
        const c = byId.get(id);
        if (!c) return null;
        return (
          <g key={`u-${id}`}>
            <circle
              cx={c.x}
              cy={c.y}
              r={SIZE * 0.6}
              style={{
                fill: PLAYER_COLOR[unit.owner],
                stroke: token("unit-ink"),
              }}
              strokeWidth={2}
            />
            <text
              x={c.x}
              y={c.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={13}
              fontWeight={700}
              style={{ fill: token("unit-ink") }}
            >
              {unit.hp}
            </text>
          </g>
        );
      })}

      {/* Emphasis rings: own selection (blue) and the unit under attack (red). */}
      {[
        { cell: selectedCell, stroke: token("select"), key: "select" },
        { cell: dangerCell, stroke: token("danger"), key: "danger" },
      ].map(({ cell, stroke, key }) => {
        const c = cell ? byId.get(cell) : undefined;
        if (!c) return null;
        return (
          <polygon
            key={`emph-${key}`}
            points={hexPoints(c.x, c.y)}
            style={{ fill: "none", stroke }}
            strokeWidth={4}
          />
        );
      })}

      {/* Interaction layer: clickable cells sit on top so clicks always land. */}
      {onCellClick
        ? LAYOUT.filter((c) => isSelectable(c.id)).map((c) => (
            <polygon
              key={`hit-${c.id}`}
              points={hexPoints(c.x, c.y)}
              strokeWidth={2}
              style={{
                fill: token("select-tint"),
                stroke: token("select"),
                cursor: "pointer",
              }}
              data-testid={`augmented-war-cell-${c.id}`}
              onClick={() => onCellClick(c.id)}
            />
          ))
        : null}
    </svg>
  );
}

export const combatGlyph = ELEMENT_GLYPH;
